<template>
	<div id="worklist" class="worklist">
		<WorklistHeader
			:worklist="worklist"
			:show_search="show_search"
			:isSaveAsBtn="show_save_as"
			@show-search="onShowSearchToggle"
			@show-cols-filter="onSearchColsFilter"
			@show-rename="onRename"
			@show-delete="onShowDelete"
			@save-as="onShowSaveDialog"
			@save="onSave"
			@print="onPrint()"
		/>
		<v-alert class="mx-5 mt-3" type="error" v-if="errormsg.length !== 0">
			{{ errormsg }}
		</v-alert>
		<ProgressLinear v-if="loading"/>
		<div class="worklist-container">
			<SearchAdvance 
				v-if="!loading && show_search"
				:class="$vuetify.breakpoint.smAndDown ?'search-result-single' : 'search-filter' " 
				:title="worklist.name === 'All live jobs' ? 'All Live and Proposed jobs' : worklist.name"
				:settings="setting_param"
				:statusses="statusses"
				:job_types="job_types"
				:isClose="true"
				@advanced-filter="onShowSearchToggle"
				@update-search="onUpdateSearch"
				@save-as="onShowSaveDialog"
			/>
			<SearchColsFilter 
				v-if="!loading && show_cols_filter" 
				:class="$vuetify.breakpoint.smAndDown ?'search-result-single' : 'search-filter' " 
				:selected="selected"
				:financialsPrivilege="financialsPrivilege"  
				@close="onCloseSearchColsFilter"/>
			<SearchResults
				v-if="!loading && errormsg.length === 0 && show_search_results" 
				:class="show_search || show_cols_filter ? 'search-result-multi': 'search-result-single'"
				:joblists="joblists" 
				:headers="head_kv"
				:color="$attrs.color"
				:page="page"
				:perpage="limit"
				@columns="onSearchColsFilter()"
				@header-change="header_print = $event"
				@pagination="onPagination($event)"/>
		</div>
		<RenameWorklistDialog ref="rename_worklist_dialog" @renamed="load(worklist.id)" />
		<DelWorklistDialog
			ref="del_worklist_dialog"
			@deleted="onDelete"
		/>
		<NewWorklistDialog 
			:visible="saveDialog_visible" 
			:is_saved="is_saved"
			@saved="onSaveAs"
			@cancel="saveDialog_visible = false"/>
		<v-dialog
			v-model="save_visible"
			max-width="600"
			persistent
		>
			<v-alert
				border="top"
				colored-border
				color="#00AAFD"
				class="mb-0 px-8 py-6"
			>
				<div class="title-text">Worklist ‘{{ worklist.name }}’ has been saved. </div>
			</v-alert>
		</v-dialog>
		<PrintTable
			id="print"
			style="display: none"
			v-if="!loading && errormsg.length === 0 && show_search_results" 
			:joblists="joblists" 
			:headers="header_print"/>
	</div>
</template>

<script>
import SearchAdvance from '@/components/jobs/search_advance.vue';
import SearchColsFilter from '@/components/jobs/search_cols_filter';
import SearchResults from "@/components/jobs/search_results_new";
import PrintTable from '@/components/jobs/print_table.vue';

import WorklistHeader from '@/components/worklist/worklist_header_new';
import RenameWorklistDialog from '@/components/worklist/rename_worklist_dialog';
import DelWorklistDialog from "@/components/worklist/del_worklist_dialog";
import NewWorklistDialog from "@/components/worklist/new_worklist_dialog_new";
import ProgressLinear from "@/components/progress/progress_getdata_linear";

import Worklist from '@/models/worklist.js';
import config from '@/config.js';
import { metaMixin } from "@/mixins/jobs/metaMixin";

const WORKLIST_CLASS = 'restful.jobs.worklists';
const wl = new Worklist();
const STATUS_DEFAULT = {
	Proposed: 'Proposed',
	Live: 'Live'
};

/** If selected the user will be able to access the Financial tab in the Jobs window */
const JOf = 'JOf';
const Financials_Group = ['total_value', 'perc_quoted_billed', 'total_invoiced', 'perc_value_billed', 'perc_traffic_used'];

export default {
	name: 'Worklist',
	mixins: [ metaMixin ],
	components: {
		WorklistHeader,
		SearchResults, 
		SearchAdvance,
		SearchColsFilter, 
		RenameWorklistDialog,
		DelWorklistDialog,
		NewWorklistDialog,
		PrintTable,
		ProgressLinear
	},
	data() {
		return {
			worklist: new Worklist(),
			loading: true,
			errormsg: '',
			show_search: false,
			show_cols_filter: false,
			show_search_results: true,
			show_save_as: false,
			financialsPrivilege: true,
			head_kv: [],
			joblists: [],
			setting_param: null,
			is_saved: false,
			saveDialog_visible: false,
			save_visible: false,
			tid: null,
			tid0: null,
			header_print: [],
			page: 1,
			start: 0,
			limit: this.$vuetify.breakpoint.smAndDown ? 10: config.countPerpage,
		};
	},
	async created() {
		await this.load_meta_data();
		await this.getFinancialsPrivilege();

		if (this.financialsPrivilege) {
			this.head_kv = config.head_kv;
		} else {
			const head_tmp = JSON.parse(JSON.stringify(config.head_kv));
			head_tmp.forEach(h => {
				if (Financials_Group.includes(h.value)) {
					h.showable = false;
				}
			});
			this.head_kv = head_tmp;
		}

		this.header_print = this.head_kv;
		this.load(this.$route.params.id);
	},
	beforeDestroy() {
		if(this.tid) {
			clearTimeout(this.tid);
		}
		if(this.tid0) {
			clearTimeout(this.tid0);
		}
	},
	watch: {
		head_kv: {
			handler() {
				// header_print changed depend on head_kv
				this.header_print = this.head_kv;
			},
			deep: true,
		}
	},
	computed: {
		selected() {
			const arr = [];
			this.head_kv.forEach(item => {
				if (item.showable) {
					const obj = {
						text: item.text,
						value: item.value,
					}
					arr.push(obj);
				}
			});
			return arr;
		},
	},
	methods: {
		load(wl_id) {
			this.worklist.load(wl_id).then(() => {
				/* add this page to our breadcrumbs */
				this.$store.commit("push_breadcrumb", {
					exact: true,
					text: this.worklist.name,
					to: this.$route.path
				});

				let setting = null;
				const str = JSON.parse(this.worklist.settings);

				if (typeof(str) === 'string') {
					setting = JSON.parse(str);
				} else {
					setting = str;
				}
				
				if (this.worklist.owner && this.worklist.owner.is_admin) {
					const statuses_arr = this.statusses.filter(status => {
						return status.desc === STATUS_DEFAULT.Proposed || status.desc === STATUS_DEFAULT.Live;
					});
					const statuses = statuses_arr.map(status => { return status.id });
					setting.statusses = statuses;
				}

				this._onSearch(setting);	
				
			}).catch(e => {
				console.log(e)
			})

		},
		async getFinancialsPrivilege() {
			const url = "/api/user/me/privileges";
			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}					
				})
				.then(val => {
					const {status, data}  = val.data;

					if (status === "OK") {
						if (data.includes(JOf)) {
							this.financialsPrivilege = true;
						} else {
							this.financialsPrivilege = false;
						}
					}
				})
				.catch((e) => {
					this.errormsg = e.message;
				});
		},
		onShowSearchToggle() {
			if (this.$vuetify.breakpoint.smAndDown) {
				this.show_search = !this.show_search;
				if (this.show_cols_filter) {
					this.show_cols_filter = false;
				}
				this.show_search_results = !this.show_search_results;
			} else {
				this.show_search = !this.show_search;
				if (this.show_cols_filter) {
					this.show_cols_filter = false;
				}
			}
			
		},
		onUpdateSearch(event) {
			this.setting_param = event;
			this.show_save_as = true;
			this.show_search_results = true;
			this._onSearch(event);	
		},
		onPagination(pagination) {
			const limit_res = pagination.limit;
			
			const start_tmp = (pagination.page - 1) * pagination.limit;
			const page_tmp = Math.ceil(pagination.total / pagination.limit);
			const page_res = start_tmp > pagination.total ? page_tmp : pagination.page;
			const start_res = start_tmp > pagination.total ? (page_res - 1) * pagination.limit : start_tmp;

			const obj = {
				page: page_res,
				limit: limit_res,
				start: start_res
			}
			this._onSearch(this.setting_param, obj);
		},
		onShowSaveDialog() {
			this.saveDialog_visible = true;
		},
		onSaveAs(params) {
			wl.id = 0;
			wl.class = WORKLIST_CLASS;
			wl.name = params.name;
			wl.is_dynamic = params.is_dynamic;
			wl.is_owned = true;
			wl.settings = this.setting_param;
			
			wl.create()
				.then(() => {
					this.is_saved = true;
					this.show_save_as = false;
					this.tid = setTimeout(this._close, 2000);
					this.tid0 = setTimeout(this._setInit, 2500);
				})
				.catch((reason) => {
					this.errormsg = reason;
					this._close();
				});
		},
		onSave() {
			this.worklist.settings = this.setting_param;

			this.worklist.save()
				.then(() => {
					this.show_save_as = false;
					this.save_visible = true;
					this.tid = setTimeout(this._close, 2000);
				})
				.catch((reason) => {
					this.errormsg = reason;
					this._close();
				});
		},
		onSearchColsFilter() {
			if (this.$vuetify.breakpoint.smAndDown) {
				this.show_search = false;
				this.show_cols_filter = true;
				this.show_search_results = false;
			} else {
				this.show_search = false;
				this.show_cols_filter = !this.show_cols_filter;
			}
		},
		onCloseSearchColsFilter(event) {
			if (event) {
				const settings_tmp = typeof(this.worklist.settings) === 'string' ?  JSON.parse(this.worklist.settings) : this.worklist.settings;
				let settings_obj = null;
				if (typeof(settings_tmp) === 'object') {
					settings_obj = settings_tmp;
				} else {
					settings_obj = typeof(JSON.parse(settings_tmp)) === 'string' ? JSON.parse(JSON.parse(settings_tmp)) : JSON.parse(settings_tmp); 
				}

				settings_obj.show_columns = event.map(items => { return items.value});

				this.worklist.settings = JSON.stringify(settings_obj); 
				this.worklist.save().then(() => {
					const event_arr = event.map(items => { return items.text});
					this.head_kv.forEach(head => {
						if (event_arr.includes(head['text'])) {
							head['showable'] = true;
						} else {
							head['showable'] = false;
						}
					})
				}).catch(err => {
					this.errormsg = err;
				});
			}

			if (this.$vuetify.breakpoint.smAndDown) {
				this.show_cols_filter = false;
				this.show_search = false;
				this.show_search_results = true;
			} else {
				this.show_cols_filter = false;
			}
			
		},
		onRename() {
			this.$refs.rename_worklist_dialog.open(this.worklist);
		},
		onCopyWorklist(event) {
			this.load(event.id);
			this.$router.push({ name: "Job Worklist", params: { id: event.id }});
		},
		onShowDelete() {
			this.$refs.del_worklist_dialog.open(this.worklist);
		},
		onDelete() {
			this.$router.push({name:'Saved Job Worklists'});
		},
		onPrint() {
			const printHtml = document.getElementById("print").innerHTML;
			const printFrame = document.createElement('iframe');
			printFrame.setAttribute('style', 'visibility: hidden; height: 0; width: 0; position: absolute;');
			printFrame.srcdoc = printHtml;
			
			document.body.appendChild(printFrame);
			setTimeout(function(){ 
				printFrame.contentWindow.print();
				document.body.removeChild(printFrame);
			},500);
		},
		_onSearch(setting, params_obj) {
			this.loading = true;
      
			const params = [];
			if (setting.from_code && setting.from_code.length !== 0) params.push("from_code=" + encodeURIComponent(setting.from_code));
			if (setting.to_code && setting.to_code.length !== 0) params.push("to_code=" + encodeURIComponent(setting.to_code));
			if (setting.short && setting.short.length !== 0) params.push("short=" + encodeURIComponent(setting.short));
			if (setting.job_types && setting.job_types.length !== 0) params.push("job_types=" + encodeURIComponent(setting.job_types));
			if (setting.statusses && setting.statusses.length !== 0) params.push("statusses=" + encodeURIComponent(setting.statusses));
			if (setting.status_groups && setting.status_groups.length !== 0) params.push("status_groups=" + encodeURIComponent(setting.status_groups));
			if (setting.client && (setting.client !== 0 || setting.client.length !== 0)) params.push("client=" + encodeURIComponent(setting.client));
			if (setting.invoice_from != null) params.push("invoice_from=" + setting.invoice_from);
			if (setting.invoice_to != null) params.push("invoice_to=" + setting.invoice_to);
			if (setting.deadline_from != null) params.push("deadline_from=" + setting.deadline_from);
			if (setting.deadline_to != null) params.push("deadline_to=" + setting.deadline_to);
			if (setting.entered_from != null) params.push("entered_from=" + setting.entered_from);
			if (setting.entered_to != null) params.push("entered_to=" + setting.entered_to);
			if (setting.opened_from != null) params.push("opened_from=" + setting.opened_from);
			if (setting.opened_to != null) params.push("opened_to=" + setting.opened_to);
			if (setting.status_change_from != null) params.push("status_change_from=" + setting.status_change_from);
			if (setting.status_change_to != null) params.push("status_change_to=" + setting.status_change_to);
			if (setting.made_dead_from != null) params.push("made_dead_from=" + setting.made_dead_from);
			if (setting.made_dead_to != null) params.push("made_dead_to=" + setting.made_dead_to);

			this.limit = params_obj ? params_obj.limit : (this.$vuetify.breakpoint.smAndDown ? 10:config.countPerpage);
			this.page = params_obj ? params_obj.page : 1;
			this.start = params_obj ? params_obj.start : 0;

			params.push('limit' + '=' + encodeURIComponent(this.limit));
			params.push('start' + '=' + encodeURIComponent(this.start));
			params.push('is_lookup' + '=' + encodeURIComponent(false));

			let url = "/api/jobs";
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}
			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const json = val.data;

					if ((typeof json) == "string") {
						json = JSON.parse(json);
					}

					const { status, data,  reason} = json;

					if (status === "OK") {
						if (setting.show_columns && setting.show_columns.length !== 0) {
							const obj = JSON.parse(JSON.stringify(config.head_kv));

							obj.forEach(item => {
								if (this.financialsPrivilege) {
									if (setting.show_columns.includes(item.value)) {
										item.showable = true;
									} else {
										item.showable = false;
									}
								} else {
									if (setting.show_columns.includes(item.value) && !Financials_Group.includes(item.value)) {
										item.showable = true;
									} else {
										item.showable = false;
									}
								}
								
							});
							this.head_kv = obj;

						} else {
							if (this.financialsPrivilege) {
								this.head_kv = config.head_kv;
							} else {
								const head_tmp = JSON.parse(JSON.stringify(config.head_kv));
								head_tmp.forEach(h => {
									if (Financials_Group.includes(h.value)) {
										h.showable = false;
									}
								});
								this.head_kv = head_tmp;
							}
						}	

						this.joblists = data;
						this.setting_param = setting;
						this.show_search = false;
						this.loading = false;
					} else {
						this.errormsg = reason;
					}
				})
				.catch(e => {
					this.errormsg = e.message;
				})
				.finally(() => {
					this.loading = false;
				});	
		},
		_close() {
			this.saveDialog_visible = false;
			this.save_visible = false;
		},
		_setInit() {
			this.is_saved = false;
		}
	},
};
</script>

<style scoped>
.worklist {
  min-width: 100%;
  min-height: 100%;
  /* display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center; */
}
.worklist-container {
	padding: 20px;
	width: 100%;
	height: 100%;
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
}
.search-filter {
	width: 24%;
}
.search-result-single {
	width: 100%;
}
.search-result-multi {
	width: 75%;
}

</style>
